import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminAPI } from '../../utils/api';
import { Star, Trash2, Check, Search, X } from 'lucide-react';
import toast from 'react-hot-toast';

export default function AdminUsersPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState('users');
  const [search, setSearch] = useState('');

  const { data: users = [], isLoading } = useQuery({ queryKey:['admin-users'], queryFn: () => adminAPI.getUsers().then(r => r.data) });
  const { data: reviews = [], isLoading: revLoading } = useQuery({ queryKey:['admin-reviews'], queryFn: () => adminAPI.getReviews().then(r => r.data) });

  const pendingReviews = reviews.filter(r => !r.is_approved);
  const approvedReviews = reviews.filter(r => r.is_approved);

  const approveRevMut = useMutation({
    mutationFn: adminAPI.approveReview,
    onSuccess: () => { toast.success('Vlerësimi u miratua!'); qc.invalidateQueries(['admin-reviews']); },
    onError: () => toast.error('Gabim'),
  });
  const deleteRevMut = useMutation({
    mutationFn: adminAPI.deleteReview,
    onSuccess: () => { toast.success('Vlerësimi u fshi'); qc.invalidateQueries(['admin-reviews']); },
  });

  const filteredUsers = users.filter(u => !search ||
    u.name.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div style={{ marginBottom:24 }}>
        <h1 style={{ fontSize:'1.6rem', marginBottom:4 }}>Përdoruesit & Vlerësimet</h1>
        <p style={{ color:'var(--text-light)', fontSize:'.88rem' }}>{users.length} përdorues · {pendingReviews.length} vlerësime në pritje</p>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:4, background:'white', borderRadius:'var(--radius-md)', padding:4, border:'1px solid var(--cream-dark)', width:'fit-content', marginBottom:20 }}>
        {[['users','Përdoruesit'],['reviews_pending',`Vlerësime në Pritje (${pendingReviews.length})`],['reviews_approved','Vlerësime të Miratuara']].map(([k,l]) => (
          <button key={k} onClick={() => setTab(k)} style={{ padding:'7px 16px', borderRadius:'var(--radius-sm)', border:'none', cursor:'pointer', fontSize:'.82rem', fontWeight:500, background:tab===k?'var(--green-deep)':'transparent', color:tab===k?'white':'var(--text-light)', transition:'all .2s' }}>{l}</button>
        ))}
      </div>

      {/* USERS */}
      {tab === 'users' && (
        <>
          <div style={{ display:'flex', alignItems:'center', gap:10, background:'white', border:'1.5px solid var(--cream-dark)', borderRadius:'var(--radius-md)', padding:'6px 14px', marginBottom:20, color:'var(--text-light)' }}>
            <Search size={16} />
            <input placeholder="Kërko emrin ose email..." value={search} onChange={e => setSearch(e.target.value)}
              style={{ flex:1, border:'none', outline:'none', fontFamily:'inherit', fontSize:'.875rem', background:'transparent', color:'var(--text-dark)' }} />
            {search && <button onClick={() => setSearch('')} style={{ border:'none', background:'none', cursor:'pointer' }}><X size={14} /></button>}
          </div>
          <div className="card" style={{ padding:0, overflow:'hidden' }}>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Emri</th><th>Email</th><th>Roli</th><th>Telefoni</th><th>Porositë</th><th>Regjistrimi</th></tr></thead>
                <tbody>
                  {isLoading && <tr><td colSpan={6} style={{ textAlign:'center', padding:40 }}><div className="spinner" style={{ margin:'0 auto' }} /></td></tr>}
                  {filteredUsers.map(u => (
                    <tr key={u.id}>
                      <td>
                        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                          <div style={{ width:34, height:34, borderRadius:'50%', background: u.role==='admin' ? 'var(--gold)' : 'var(--green-soft)', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:'.85rem', color: u.role==='admin' ? 'var(--green-deep)' : 'var(--green-deep)', flexShrink:0 }}>
                            {u.name?.[0]?.toUpperCase()}
                          </div>
                          <span style={{ fontWeight:500 }}>{u.name}</span>
                        </div>
                      </td>
                      <td style={{ fontSize:'.85rem' }}>{u.email}</td>
                      <td><span className={`badge ${u.role==='admin'?'badge-gold':'badge-green'}`}>{u.role}</span></td>
                      <td style={{ fontSize:'.85rem', color:'var(--text-light)' }}>{u.phone || '—'}</td>
                      <td><span className="badge badge-blue">{u.order_count}</span></td>
                      <td style={{ fontSize:'.82rem', color:'var(--text-light)' }}>{new Date(u.created_at).toLocaleDateString('sq')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* REVIEWS PENDING */}
      {tab === 'reviews_pending' && (
        <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
          {revLoading && <div className="spinner" style={{ margin:'40px auto' }} />}
          {!revLoading && pendingReviews.length===0 && (
            <div style={{ textAlign:'center', padding:60, background:'white', borderRadius:'var(--radius-xl)', color:'var(--text-light)' }}>
              <Star size={40} strokeWidth={1} style={{ margin:'0 auto 12px', color:'var(--gold)' }} />
              <p>Nuk ka vlerësime në pritje.</p>
            </div>
          )}
          {pendingReviews.map(r => (
            <ReviewCard key={r.id} review={r}
              onApprove={() => approveRevMut.mutate(r.id)}
              onDelete={() => { if(window.confirm('Fshi vlerësimin?')) deleteRevMut.mutate(r.id); }}
              approving={approveRevMut.isPending}
            />
          ))}
        </div>
      )}

      {/* REVIEWS APPROVED */}
      {tab === 'reviews_approved' && (
        <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
          {revLoading && <div className="spinner" style={{ margin:'40px auto' }} />}
          {!revLoading && approvedReviews.length===0 && (
            <div style={{ textAlign:'center', padding:60, background:'white', borderRadius:'var(--radius-xl)', color:'var(--text-light)' }}>Nuk ka vlerësime të miratuara.</div>
          )}
          {approvedReviews.map(r => (
            <ReviewCard key={r.id} review={r} approved
              onDelete={() => { if(window.confirm('Fshi vlerësimin?')) deleteRevMut.mutate(r.id); }}
            />
          ))}
        </div>
      )}

      <style>{`
        .card { background:white; border-radius:var(--radius-xl); border:1px solid var(--cream-dark); }
      `}</style>
    </div>
  );
}

function ReviewCard({ review: r, onApprove, onDelete, approved, approving }) {
  return (
    <div className="card" style={{ padding:18 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:12 }}>
        <div style={{ flex:1 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
            <div style={{ width:32, height:32, borderRadius:'50%', background:'var(--green-soft)', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:'.82rem', color:'var(--green-deep)' }}>
              {(r.user_name||'A')[0]}
            </div>
            <div>
              <div style={{ fontWeight:600, fontSize:'.88rem' }}>{r.user_name || 'Anonim'}</div>
              <div style={{ color:'var(--gold)', fontSize:'.82rem' }}>{'★'.repeat(r.rating)}{'☆'.repeat(5-r.rating)}</div>
            </div>
            <div style={{ marginLeft:'auto', fontSize:'.75rem', color:'var(--text-light)' }}>Produkti: <strong>{r.product_name}</strong></div>
          </div>
          {r.title && <div style={{ fontWeight:600, fontSize:'.88rem', marginBottom:4 }}>{r.title}</div>}
          <p style={{ fontSize:'.88rem', color:'var(--text-mid)', lineHeight:1.6 }}>{r.body}</p>
          {r.verified_purchase && <span className="badge badge-green" style={{ marginTop:6 }}>✓ Blerje e Verifikuar</span>}
        </div>
        <div style={{ display:'flex', gap:8, flexShrink:0 }}>
          {!approved && onApprove && (
            <button className="btn btn-primary btn-sm" onClick={onApprove} disabled={approving}>
              <Check size={14} /> Mirато
            </button>
          )}
          <button className="btn btn-ghost btn-sm" style={{ color:'var(--danger)' }} onClick={onDelete}>
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
